import { AssetsService } from '@inference.engine/assets-api-client/web';
import { useMutation } from '@tanstack/react-query';
import { assetsHttp } from '@/lib/api/assets-client';
import type { UploadedAsset } from '../model/types';

export interface UploadAssetInput {
  file: File;
  /** Fraction between 0 and 1. Only fires when the response carries a total. */
  onProgress?: (fraction: number) => void;
  signal?: AbortSignal;
}

/**
 * A bare `File` or the same file with progress and cancellation.
 *
 * Kept as a union rather than moving every caller onto the object form: sixteen call sites pass a
 * `File`, and `AssetUploader` in `entities/brand-media` is a structural interface three components
 * satisfy by handing this hook straight in. Widening the variable is free; migrating them is not,
 * and none of them want progress.
 */
export type UploadAssetVariables = File | UploadAssetInput;

const normalise = (variables: UploadAssetVariables): UploadAssetInput =>
  variables instanceof File ? { file: variables } : variables;

/**
 * Uploads one file and returns the asset the service created.
 *
 * Callers keep the returned `id` on whatever record the file belongs to, and
 * never the `url` — it is signed and expires. Read it back through
 * `assetUrlsQueryOptions`.
 */
export function useUploadAsset() {
  return useMutation({
    mutationFn: async (variables: UploadAssetVariables): Promise<UploadedAsset> => {
      const { file, onProgress, signal } = normalise(variables);

      const response = await AssetsService.uploadAsset({
        client: assetsHttp,
        body: { file },
        signal,
        // The generated client spreads its options into the axios call, so this reaches axios
        // without the generated code needing to know about it.
        onUploadProgress: (event: { loaded: number; total?: number }) => {
          if (onProgress && event.total) onProgress(event.loaded / event.total);
        },
      });

      if (!response.data) {
        throw new Error('Failed to upload the file');
      }

      return {
        id: response.data.id,
        originalFilename: response.data.originalFilename,
        mimeType: response.data.mimeType,
        url: response.data.url,
      };
    },
  });
}
